import React from 'react';
import { Form, Select } from 'antd';
import type { FormInstance, SelectProps } from 'antd';
import { DEFAULT_FONT_FAMILY, FONT_FAMILY_OPTIONS, type FontOption } from '../../utils/appFonts';
import { getBaseFontFamilyToken, hasFontFamilyValue, resolveFontFamily } from '../../utils/fontFamily';
import { styleFieldName } from '../../utils/styleFormFieldNames';
import { applyDedicatedTypographyToForm } from './fontFamilyFormSync';

type Props = SelectProps;

type FormSyncProps = {
  form: FormInstance;
  namePrefix?: string | string[];
};

const FontFamilySelectControl = React.forwardRef<any, Props>(function FontFamilySelectControl(
  { className, style, ...rest },
  ref,
) {
  const value = rest.value;
  const hasFont = hasFontFamilyValue(value);
  const resolved = hasFont ? resolveFontFamily(String(value)) : undefined;

  return (
    <Select
      ref={ref}
      size="small"
      allowClear={false}
      placeholder="Font"
      className={['word-ribbon-font-select', hasFont ? 'word-ribbon-font-select--active' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={{
        ...style,
        ...(resolved ? { fontFamily: resolved } : {}),
      }}
      popupMatchSelectWidth={false}
      options={FONT_FAMILY_OPTIONS.map((opt: FontOption) => ({
        ...opt,
        label: (
          <span style={opt.value ? { fontFamily: resolveFontFamily(String(opt.value)) } : undefined}>
            {opt.label}
          </span>
        ),
      }))}
      {...rest}
    />
  );
});

export const FontFamilySelect = FontFamilySelectControl;

const FontFamilySelectWithFormSync = React.forwardRef<
  any,
  FormSyncProps & Pick<SelectProps, 'value' | 'onChange' | 'className' | 'style'>
>(function FontFamilySelectWithFormSync({ form, namePrefix, value, onChange, ...rest }, ref) {
  const displayValue = value ? getBaseFontFamilyToken(value) ?? value : DEFAULT_FONT_FAMILY;

  return (
    <FontFamilySelectControl
      ref={ref}
      {...rest}
      value={displayValue}
      onChange={(next) => {
        const base = typeof next === 'string' && next.trim() ? next : DEFAULT_FONT_FAMILY;
        onChange?.(base);
        applyDedicatedTypographyToForm(form, namePrefix);
      }}
    />
  );
});

/** Font family picker — stores base family; bold/italic toggles pick the font file on save. */
export const FontFamilyFormSelect: React.FC<FormSyncProps> = ({ form, namePrefix }) => (
  <Form.Item name={styleFieldName(namePrefix, 'fontFamily')} noStyle>
    <FontFamilySelectWithFormSync form={form} namePrefix={namePrefix} />
  </Form.Item>
);
